import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Check, Plus, Trash2, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  usePayments,
  useCreatePayment,
  useUpdatePayment,
  useDeletePayment,
  type Payment,
} from "@/hooks/usePayments";
import { useToast } from "@/hooks/useToast";

const paymentSchema = z.object({
  payment_type: z.enum(["Deposit", "Balance"]),
  amount: z.coerce.number().min(0.01, "Le montant doit être supérieur à 0"),
  due_date: z.string().optional(),
});

type PaymentFormData = z.infer<typeof paymentSchema>;

interface PaymentTrackingProps {
  quoteId: string | undefined;
  totalAmount: number;
}

export function PaymentTracking({
  quoteId,
  totalAmount,
}: PaymentTrackingProps) {
  const { data: payments = [] } = usePayments(quoteId);
  const createPayment = useCreatePayment();
  const updatePayment = useUpdatePayment();
  const deletePayment = useDeletePayment();
  const { toast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      payment_type: "Deposit",
      amount: 0,
      due_date: "",
    },
  });

  const depositPayment = payments.find((p) => p.payment_type === "Deposit");
  const balancePayment = payments.find((p) => p.payment_type === "Balance");

  const suggestedDeposit = totalAmount * 0.3;
  const suggestedBalance = totalAmount - (depositPayment?.amount ?? 0);

  const openNewPaymentDialog = (type: "Deposit" | "Balance") => {
    setEditingPayment(null);
    reset({
      payment_type: type,
      amount: type === "Deposit" ? suggestedDeposit : suggestedBalance,
      due_date: "",
    });
    setDialogOpen(true);
  };

  const openEditPaymentDialog = (payment: Payment) => {
    setEditingPayment(payment);
    reset({
      payment_type: payment.payment_type as "Deposit" | "Balance",
      amount: payment.amount,
      due_date: payment.due_date ?? "",
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data: PaymentFormData) => {
    if (!quoteId) return;

    try {
      if (editingPayment) {
        await updatePayment.mutateAsync({
          id: editingPayment.id,
          updates: {
            ...data,
            due_date: data.due_date || null,
          },
        });
        toast({ title: "Paiement mis à jour" });
      } else {
        await createPayment.mutateAsync({
          quote_id: quoteId,
          ...data,
          due_date: data.due_date || null,
          is_paid: false,
        });
        toast({ title: "Paiement créé" });
      }
      setDialogOpen(false);
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  const handleMarkAsPaid = async (payment: Payment) => {
    try {
      await updatePayment.mutateAsync({
        id: payment.id,
        updates: {
          is_paid: true,
          payment_date: new Date().toISOString().split("T")[0],
        },
      });
      toast({ title: "Paiement marqué comme reçu" });
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (paymentId: string) => {
    try {
      await deletePayment.mutateAsync(paymentId);
      toast({ title: "Paiement supprimé" });
    } catch {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  if (!quoteId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Paiements</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Créez d'abord un devis pour gérer les paiements.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Paiements</CardTitle>
          <p className="text-sm text-muted-foreground">
            Total devis: {totalAmount.toFixed(2)}€
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Acompte */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Acompte (30%)</h4>
              {depositPayment?.is_paid ? (
                <Badge variant="default" className="bg-green-600">
                  Reçu
                </Badge>
              ) : depositPayment ? (
                <Badge variant="secondary">En attente</Badge>
              ) : null}
            </div>

            {depositPayment ? (
              <div className="space-y-2">
                <p className="text-2xl font-bold">
                  {depositPayment.amount.toFixed(2)}€
                </p>
                {depositPayment.due_date && (
                  <p className="text-sm text-muted-foreground">
                    Échéance:{" "}
                    {format(new Date(depositPayment.due_date), "d MMMM yyyy", {
                      locale: fr,
                    })}
                  </p>
                )}
                {depositPayment.payment_date && (
                  <p className="text-sm text-green-600">
                    Reçu le:{" "}
                    {format(
                      new Date(depositPayment.payment_date),
                      "d MMMM yyyy",
                      { locale: fr },
                    )}
                  </p>
                )}
                <div className="flex gap-2 mt-2">
                  {!depositPayment.is_paid && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkAsPaid(depositPayment)}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Marquer comme reçu
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditPaymentDialog(depositPayment)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(depositPayment.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => openNewPaymentDialog("Deposit")}
              >
                <Plus className="h-4 w-4 mr-1" />
                Créer l'acompte ({suggestedDeposit.toFixed(2)}€)
              </Button>
            )}
          </div>

          {/* Solde */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Solde</h4>
              {balancePayment?.is_paid ? (
                <Badge variant="default" className="bg-green-600">
                  Reçu
                </Badge>
              ) : balancePayment ? (
                <Badge variant="secondary">En attente</Badge>
              ) : null}
            </div>

            {balancePayment ? (
              <div className="space-y-2">
                <p className="text-2xl font-bold">
                  {balancePayment.amount.toFixed(2)}€
                </p>
                {balancePayment.due_date && (
                  <p className="text-sm text-muted-foreground">
                    Échéance:{" "}
                    {format(new Date(balancePayment.due_date), "d MMMM yyyy", {
                      locale: fr,
                    })}
                  </p>
                )}
                {balancePayment.payment_date && (
                  <p className="text-sm text-green-600">
                    Reçu le:{" "}
                    {format(
                      new Date(balancePayment.payment_date),
                      "d MMMM yyyy",
                      { locale: fr },
                    )}
                  </p>
                )}
                <div className="flex gap-2 mt-2">
                  {!balancePayment.is_paid && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkAsPaid(balancePayment)}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Marquer comme reçu
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditPaymentDialog(balancePayment)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(balancePayment.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => openNewPaymentDialog("Balance")}
                disabled={!depositPayment}
              >
                <Plus className="h-4 w-4 mr-1" />
                Créer le solde ({suggestedBalance.toFixed(2)}€)
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPayment ? "Modifier le paiement" : "Créer un paiement"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label>Type de paiement</Label>
              <Select
                defaultValue={editingPayment?.payment_type ?? "Deposit"}
                onValueChange={(v) =>
                  setValue("payment_type", v as "Deposit" | "Balance")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Deposit">Acompte</SelectItem>
                  <SelectItem value="Balance">Solde</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Montant (€)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                {...register("amount")}
              />
              {errors.amount && (
                <p className="text-sm text-red-500">{errors.amount.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Date d'échéance</Label>
              <Input id="due_date" type="date" {...register("due_date")} />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
